// Setup guide for the Digital Display Stand (the Setup Guide dropdown on its panel).
// The app wording here matches App/Android's MainActivity.kt, and the images in
// public/guide are mockups of those same screens.

const GRADIENT = 'linear-gradient(135deg,#04aff7,#f42ae4)';

const STEPS = [
  {
    title: 'Install the app',
    text: 'On your Android phone, tap the button below and open the file when it finishes. If your phone asks, allow installs from your browser, then tap Install.',
    note: 'The app needs Android 9 or newer. An iOS app will be released soon.',
    download: true,
    image: { src: '/guide/app-install.webp', width: 768, height: 860, alt: 'Android asking whether to install Cheeks Display, with a tap on Install', tap: { left: 70.5, top: 69.3, width: 17, height: 9.5, radius: 8, x: 79, y: 74.2 } },
  },
  {
    title: 'Plug in your stand',
    text: 'Connect the USB-C port on the back of the stand to a USB charger or computer. Then turn on Bluetooth on your phone.',
    photo: { src: '/digital_stand_rear.png', alt: 'The back of the stand, with a USB-C cable plugging into the port at the bottom', plug: true },
  },
  {
    title: 'Tap your stand',
    text: 'Open Cheeks Display and allow Nearby devices when asked. Your stand appears within a few moments with a green Ready light. Tap it.',
    note: 'On Android 11 or older, the app asks for location access instead. It uses this only to find the stand.',
    image: { src: '/guide/app-stands.webp', width: 768, height: 620, alt: 'The app listing a stand called Cheeks 2.9in A1B2 with a green Ready light, with a tap on it', tap: { left: 7, top: 48.1, width: 85.7, height: 32.3, radius: 11, x: 76, y: 64.2 } },
  },
  {
    title: 'Add a picture',
    text: 'Tap Add and choose a picture from your phone. Drag and zoom it to fit the box, then tap Save. It stays in the app, ready to use again.',
    note: 'The app makes its own copy, so it doesn\'t need access to your photos. Your original isn\'t changed.',
    image: { src: '/guide/app-picture.webp', width: 768, height: 1200, alt: 'The app fitting a picture inside a box the shape of the stand\'s screen, with a tap on Save', tap: { left: 7, top: 84, width: 33.2, height: 8.7, radius: 16, x: 23.6, y: 88.3 } },
  },
  {
    title: 'Send it to the stand',
    text: 'Tap a saved picture to preview exactly how it will look, then tap Send to stand. Keep your phone close until you see Done! The screen may flash while it changes, which is normal.',
    progress: true,
    image: { src: '/guide/app-send.webp', width: 768, height: 1368, alt: 'The stand\'s page in the app, previewing a saved picture in black and white, with a tap on the Send to stand button', tap: { left: 7, top: 90.2, width: 85.7, height: 7.6, radius: 16, x: 50, y: 94, loading: true } },
  },
];

const PROGRESS = [
  { label: 'Sending... 64%', color: '#a1a1aa' },
  { label: 'Updating screen...', color: '#04aff7' },
  { label: 'Done!', color: '#22c55e' },
];

const TIPS = [
  { title: 'Use a wide picture', text: 'The screen is 296 × 128 pixels, so wide pictures fit best. To change a crop later, tap the picture, then Adjust.' },
  { title: 'Keep it bold', text: 'The screen shows black and white dots, so simple shapes and strong contrast look best.' },
  { title: 'Change it any time', text: 'Plug the stand in, open the app, tap your stand and send a new picture. There\'s nothing to set up again.' },
  { title: 'More than one stand', text: 'Each stand has its own code, like A1B2. Plug in one at a time to see which code is which, then rename them from the three dots on the stand\'s page. Stands of the same size share their saved pictures.' },
  { title: 'Stands stay on your list', text: 'A stand you\'ve opened shows Not in range while it\'s off, and you can still open it to change its pictures. It turns green by itself when you plug it back in. To remove it, tap the three dots at the top of the app, then Forget a stand.' },
  { title: 'Updates', text: 'The app tells you when there\'s a new version. For new stand firmware, open your stand and tap Update. Your picture stays on the screen.' },
];

const PROBLEMS = [
  { message: 'No stands found', fix: 'Check the stand is plugged in, then tap Scan again.' },
  { message: 'Not in range', fix: 'Check the stand is plugged in and your phone is close. Your stand will connect instantly and turn green.' },
  { message: 'The app needs Bluetooth permission', fix: 'In your phone\'s Settings, open Apps, then Cheeks Display, then Permissions, and allow Nearby devices.' },
  { message: 'Couldn\'t open that picture', fix: 'Save the picture as a JPG or PNG and try again.' },
  { message: 'The stand rejected the update', fix: 'Your stand is still on its current firmware and works as normal. Close and reopen the app, then try again.' },
];

const SectionTitle = ({ children }) => (
  <h3 className="text-white font-semibold" style={{ fontSize: '0.95rem' }}>{children}</h3>
);

// A fingertip pressing the button the step talks about. Positions are percentages of the mockup.
const TapAnimation = ({ tap }) => (
  <span aria-hidden="true">
    <span className="tap-target" style={{ left: `${tap.left}%`, top: `${tap.top}%`, width: `${tap.width}%`, height: `${tap.height}%`, borderRadius: `${tap.radius}px` }} />
    {tap.loading && (
      <>
        <span className="tap-loading" style={{ left: `${tap.left}%`, top: `${tap.top}%`, width: `${tap.width}%`, height: `${tap.height}%`, borderRadius: `${tap.radius}px` }}>
          <span className="tap-loading-fill" />
          <span className="tap-loading-label" />
        </span>
        <img className="tap-sent" src="/guide/app-sent-status.webp" alt="" style={{ left: '6.51%', top: '42.98%', width: '39.97%' }} />
      </>
    )}
    <span className="tap-ripple" style={{ left: `${tap.x}%`, top: `${tap.y}%` }} />
    <span className="tap-finger" style={{ left: `${tap.x}%`, top: `${tap.y}%` }} />
  </span>
);

// A USB-C cable sliding into the port on the rear photo. Drawn in the photo's own pixels, 1024 x 857.
const UsbPlugAnimation = () => (
  <svg viewBox="0 0 1024 857" className="absolute inset-0 w-full h-full" style={{ overflow: 'visible', pointerEvents: 'none' }} aria-hidden="true">
    <defs>
      <linearGradient id="usb-body" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor="#a1a1aa" /><stop offset="0.3" stopColor="#f4f4f5" /><stop offset="0.7" stopColor="#e4e4e7" /><stop offset="1" stopColor="#a1a1aa" />
      </linearGradient>
      <linearGradient id="usb-metal" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#f4f4f5" /><stop offset="1" stopColor="#71717a" />
      </linearGradient>
    </defs>
    <circle className="plug-pulse" cx="511" cy="680" r="62" fill="none" stroke="#4ade80" strokeWidth="7" />
    <g className="plug-cable">
      <path d="M511 790 C 511 880, 500 960, 520 1700" fill="none" stroke="#d4d4d8" strokeWidth="26" strokeLinecap="round" />
      <rect x="491" y="770" width="40" height="44" rx="8" fill="#a1a1aa" />
      <rect x="465" y="694" width="92" height="88" rx="16" fill="url(#usb-body)" stroke="#52525b" strokeWidth="2" />
      <rect x="473" y="664" width="76" height="32" rx="15" fill="url(#usb-metal)" stroke="#3f3f46" strokeWidth="2" />
      <rect x="485" y="674" width="52" height="12" rx="6" fill="#27272a" />
    </g>
  </svg>
);

const Step = ({ number, step, appVersion }) => (
  <li className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_270px] gap-4 sm:gap-6 py-5 border-t border-zinc-800/70">
    <div className="min-w-0">
      <div className="flex items-center gap-2.5">
        <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm" style={{ background: GRADIENT }}>{number}</span>
        <h4 className="text-white font-semibold" style={{ fontSize: '0.95rem' }}>{step.title}</h4>
      </div>
      <p className="mt-2.5 text-zinc-300 text-sm leading-relaxed">{step.text}</p>
      {step.download && (
        <a
          href="/CheeksDisplay.apk"
          download="CheeksDisplay.apk"
          className="mt-3 flex sm:inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-white text-sm font-bold transition-transform active:scale-95"
          style={{ background: GRADIENT }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Download for Android
        </a>
      )}
      {step.download && appVersion && <p className="mt-2 text-zinc-400 text-xs">Latest version: {appVersion}</p>}
      {step.progress && (
        <ol className="mt-3 flex flex-col gap-1.5">
          {PROGRESS.map(({ label, color }) => (
            <li key={label} className="flex items-center gap-2 text-sm text-zinc-300">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
              {label}
            </li>
          ))}
        </ol>
      )}
      {step.note && <p className="mt-3 text-zinc-400 text-xs leading-relaxed">{step.note}</p>}
    </div>
    {step.image && (
      <div className="flex justify-center rounded-2xl overflow-hidden border border-zinc-800/70 px-6 pt-5" style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(4,175,247,0.14), rgba(244,42,228,0.08) 45%, rgba(0,0,0,0.35) 80%)' }}>
        <div className="relative w-full" style={{ maxWidth: '230px' }}>
          <img src={step.image.src} width={step.image.width} height={step.image.height} alt={step.image.alt} loading="lazy" className="block w-full h-auto" />
          {step.image.tap && <TapAnimation tap={step.image.tap} />}
        </div>
      </div>
    )}
    {step.photo && (
      <div className="flex items-center justify-center rounded-2xl overflow-hidden border border-zinc-800/70 p-5" style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(4,175,247,0.14), rgba(244,42,228,0.08) 45%, rgba(0,0,0,0.35) 80%)' }}>
        <div className="relative w-full" style={{ maxWidth: '200px' }}>
          <img src={step.photo.src} alt={step.photo.alt} loading="lazy" className="block w-full h-auto" />
          {step.photo.plug && <UsbPlugAnimation />}
        </div>
      </div>
    )}
  </li>
);

// The way to ask for help, at the end of the stand's guide and firmware panels
export const StillStuck = ({ onClick }) => (
  <a href="/#discord" onClick={onClick} className="flex items-center gap-3 px-3.5 py-3 rounded-xl transition-all duration-150 active:scale-95" style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.55)' }}>
    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#eab308' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-white text-sm font-semibold">Still stuck?</p>
      <p className="text-sm font-medium" style={{ color: '#facc15' }}>Get help on Discord</p>
    </div>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  </a>
);

export default function StandGuide({ onDiscord, appVersion }) {
  return (
    <div className="pt-4 pb-4">
      <p className="text-zinc-300 text-sm leading-relaxed">Your stand is controlled over Bluetooth from the Cheeks Display app. You don't need an account or Wi-Fi.</p>

      <ol className="mt-4">
        {STEPS.map((step, i) => <Step key={step.title} number={i + 1} step={step} appVersion={appVersion} />)}
      </ol>

      <div className="flex items-start gap-2.5 rounded-xl px-3.5 py-3" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
        <p className="text-zinc-200 text-sm leading-relaxed">All done! Your picture stays on the screen, even if you unplug the stand.</p>
      </div>

      <div className="mt-6">
        <SectionTitle>Good to know</SectionTitle>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          {TIPS.map((tip) => (
            <div key={tip.title} className="flex items-start gap-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium">{tip.title}</p>
                <p className="mt-0.5 text-zinc-400 text-sm leading-relaxed">{tip.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-xl overflow-hidden" style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.35)' }}>
        <div className="flex items-center gap-2.5 px-3.5 pt-3 pb-2.5">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <div className="min-w-0">
            <SectionTitle>Troubleshooting</SectionTitle>
            <p className="text-zinc-400 text-sm leading-relaxed">App messages and how to fix them.</p>
          </div>
        </div>
        <div>
          {PROBLEMS.map((problem) => (
            <div key={problem.message} className="px-3.5 py-3" style={{ borderTop: '1px solid rgba(249,115,22,0.2)' }}>
              <p className="text-sm font-medium" style={{ color: '#fdba74' }}>{problem.message}</p>
              <p className="mt-0.5 text-zinc-400 text-sm leading-relaxed">{problem.fix}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4"><StillStuck onClick={onDiscord} /></div>
    </div>
  );
}
